import type { SponsoredBrandAsset } from "@adpilot/shared";
import { IconPlayerPlay, IconSearch } from "@tabler/icons-react";
import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { AdFormat, AdPreviewPlacement } from "./sponsored-brands-model";

const AD_PREVIEW_PLACEMENTS: AdPreviewPlacement[] = [
  "topSearchMobile",
  "topSearchDesktop",
  "searchFooterDesktop",
  "searchFooterMobile",
];

const VIDEO_PREVIEW_PLACEMENTS: AdPreviewPlacement[] = ["videoMobile", "videoDesktop"];

export function SponsoredBrandsAdPreview({
  adFormat,
  brandName,
  headline,
  logoAsset,
  featureAsset,
  creativeAsins,
}: {
  adFormat: AdFormat;
  brandName: string;
  headline: string;
  logoAsset: SponsoredBrandAsset | null;
  featureAsset: SponsoredBrandAsset | null;
  creativeAsins: string[];
}) {
  const { t } = useTranslation("campaignCreation");
  const [placement, setPlacement] = useState<AdPreviewPlacement>(() =>
    defaultPreviewPlacement(adFormat),
  );
  const placementKeys = adFormat === "video" ? VIDEO_PREVIEW_PLACEMENTS : AD_PREVIEW_PLACEMENTS;
  const placementItems = placementKeys.map((value) => ({
    value,
    label: t(`sb.creative.previewPlacements.${value}`),
  }));
  const displayedBrandName = brandName || t("sb.creative.previewBrandPlaceholder");
  const displayedHeadline = headline || t("sb.creative.previewHeadlinePlaceholder");

  useEffect(() => {
    setPlacement(defaultPreviewPlacement(adFormat));
  }, [adFormat]);

  return (
    <section
      data-slot="sb-ad-preview"
      data-placement={placement}
      data-format={adFormat}
      className="self-start overflow-hidden rounded-xl border border-border bg-muted/40"
    >
      <div className="flex items-center gap-3 border-b border-border bg-background p-3">
        <p className="shrink-0 font-medium">{t("sb.creative.preview")}</p>
        <Select
          items={placementItems}
          value={placement}
          onValueChange={(value) => value && setPlacement(value as AdPreviewPlacement)}
        >
          <SelectTrigger aria-label={t("sb.creative.previewPlacementLabel")} className="min-w-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {placementItems.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
      <div className="relative min-h-96 overflow-hidden bg-[#202329] bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.22)_1px,transparent_1px)] [background-size:20px_20px] p-4 text-background">
        <p className="relative text-right text-xs">{t("sb.creative.previewWhere")}</p>
        <PreviewPlacement
          adFormat={adFormat}
          placement={placement}
          displayedBrandName={displayedBrandName}
          displayedHeadline={displayedHeadline}
          logoAsset={logoAsset}
          featureAsset={featureAsset}
          creativeAsins={creativeAsins}
          storeLabel={t("sb.creative.previewStore")}
          shopNowLabel={t("sb.creative.previewShopNow")}
        />
      </div>
    </section>
  );
}

function defaultPreviewPlacement(adFormat: AdFormat): AdPreviewPlacement {
  if (adFormat === "storeSpotlight") return "topSearchMobile";
  if (adFormat === "video") return "videoMobile";
  return "searchFooterMobile";
}

function PreviewPlacement({
  adFormat,
  placement,
  displayedBrandName,
  displayedHeadline,
  logoAsset,
  featureAsset,
  creativeAsins,
  storeLabel,
  shopNowLabel,
}: {
  adFormat: AdFormat;
  placement: AdPreviewPlacement;
  displayedBrandName: string;
  displayedHeadline: string;
  logoAsset: SponsoredBrandAsset | null;
  featureAsset: SponsoredBrandAsset | null;
  creativeAsins: string[];
  storeLabel: string;
  shopNowLabel: string;
}) {
  const usesPhone = placement === "topSearchMobile" || placement === "videoMobile";

  if (usesPhone) {
    return (
      <PreviewPhone>
        {adFormat === "video" ? (
          <>
            <PreviewBrandLine
              logoAsset={logoAsset}
              headline={displayedHeadline}
              brandName={displayedBrandName}
            />
            <PreviewVideo asset={featureAsset} compact />
          </>
        ) : (
          <PreviewStoreSpotlightMobile
            logoAsset={logoAsset}
            headline={displayedHeadline}
            brandName={displayedBrandName}
            storeLabel={storeLabel}
          />
        )}
      </PreviewPhone>
    );
  }

  if (placement === "topSearchDesktop") {
    return (
      <div className="relative mx-auto mt-5 w-full overflow-hidden bg-background text-foreground shadow-sm">
        <PreviewFeatureAsset asset={featureAsset} compact={false} />
        <PreviewBrandLine
          logoAsset={logoAsset}
          headline={displayedHeadline}
          brandName={displayedBrandName}
          storeLabel={adFormat === "storeSpotlight" ? storeLabel : null}
          bordered
        />
      </div>
    );
  }

  if (placement === "videoDesktop") {
    return (
      <div className="relative mx-auto mt-5 w-full overflow-hidden bg-background text-foreground shadow-sm">
        <PreviewBrandLine
          logoAsset={logoAsset}
          headline={displayedHeadline}
          brandName={displayedBrandName}
        />
        <PreviewVideo asset={featureAsset} compact={false} />
      </div>
    );
  }

  const mobileFooter = placement === "searchFooterMobile";
  return (
    <div
      className={cn(
        "relative mx-auto mt-5 overflow-hidden bg-background text-foreground shadow-sm",
        mobileFooter ? "w-64" : "w-full",
      )}
    >
      <PreviewFeatureAsset asset={featureAsset} compact={mobileFooter} />
      <PreviewBrandLine
        logoAsset={logoAsset}
        headline={displayedHeadline}
        brandName={displayedBrandName}
        storeLabel={adFormat === "storeSpotlight" ? storeLabel : null}
      />
      <div className="flex items-center justify-between gap-2 border-t px-3 py-2">
        <div className="flex min-w-0 gap-1.5">
          {creativeAsins.slice(0, 3).map((asin) => (
            <span
              key={asin}
              className="size-8 truncate rounded-sm bg-muted p-1 font-mono text-[8px] text-muted-foreground"
            >
              {asin}
            </span>
          ))}
        </div>
        <span className="shrink-0 text-xs font-medium">{shopNowLabel}</span>
      </div>
    </div>
  );
}

function PreviewPhone({ children }: { children: ReactNode }) {
  return (
    <div
      data-slot="sb-preview-phone"
      className="relative mx-auto mt-3 w-72 rounded-[2.75rem] border-[7px] border-[#111318] bg-[#111318] px-2 pb-4 pt-14 shadow-xl"
    >
      <span className="absolute left-1/2 top-4 h-2 w-16 -translate-x-1/2 rounded-full bg-[#2f3238]" />
      <span className="absolute left-[35%] top-[17px] size-1.5 rounded-full bg-[#2f3238]" />
      <div className="overflow-hidden bg-background text-foreground">
        <div className="flex h-11 items-center justify-between bg-[#c7c9cc] px-3 text-white">
          <span className="text-xl font-semibold tracking-tight">amazon</span>
          <span aria-hidden="true" className="text-2xl">
            🛒
          </span>
        </div>
        <div className="bg-[#e4e5e7] p-2">
          <div className="flex h-8 items-center bg-background px-2">
            <span className="h-2.5 w-24 rounded-sm bg-muted" />
            <IconSearch className="ml-auto size-4 text-muted-foreground" aria-hidden="true" />
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

function PreviewBrandLine({
  logoAsset,
  headline,
  brandName,
  storeLabel,
  bordered = false,
}: {
  logoAsset: SponsoredBrandAsset | null;
  headline: string;
  brandName: string;
  storeLabel?: string | null;
  bordered?: boolean;
}) {
  return (
    <div className="flex items-center gap-3 p-3">
      <PreviewLogo asset={logoAsset} />
      <div className={cn("min-w-0 flex-1", bordered && "border-l pl-3")}>
        <p className="truncate text-sm font-medium">{headline}</p>
        <p className="mt-1 truncate text-xs text-muted-foreground">
          {storeLabel ? `${storeLabel} ${brandName}` : brandName}
        </p>
      </div>
    </div>
  );
}

function PreviewVideo({ asset, compact }: { asset: SponsoredBrandAsset | null; compact: boolean }) {
  const { t } = useTranslation("campaignCreation");
  return (
    <div
      data-slot="sb-preview-video"
      className={cn(
        "relative flex w-full items-center justify-center overflow-hidden bg-muted",
        compact ? "h-36" : "h-28",
      )}
    >
      {asset?.url ? (
        <video
          src={asset.url}
          aria-label={t("sb.creative.previewVideo")}
          className="size-full object-cover"
          muted
          playsInline
        />
      ) : null}
      <span className="absolute flex size-10 items-center justify-center rounded-full bg-background/90 text-foreground shadow-sm">
        <IconPlayerPlay aria-hidden="true" />
      </span>
    </div>
  );
}

function PreviewStoreSpotlightMobile({
  logoAsset,
  headline,
  brandName,
  storeLabel,
}: {
  logoAsset: SponsoredBrandAsset | null;
  headline: string;
  brandName: string;
  storeLabel: string;
}) {
  return (
    <div className="bg-background p-3">
      <p className="truncate text-sm font-medium">{headline}</p>
      {logoAsset?.url ? (
        <img src={logoAsset.url} alt="" className="mt-3 size-28 object-contain" />
      ) : (
        <div className="mt-3 size-28 border bg-background" />
      )}
      <p className="mt-2 truncate text-xs text-muted-foreground">
        {storeLabel} {brandName}
      </p>
    </div>
  );
}

function PreviewFeatureAsset({
  asset,
  compact,
}: {
  asset: SponsoredBrandAsset | null;
  compact: boolean;
}) {
  if (asset?.url) {
    return (
      <img
        src={asset.url}
        alt=""
        className={cn("w-full object-cover", compact ? "h-36" : "h-28")}
      />
    );
  }
  return <div className={cn("bg-muted", compact ? "h-36" : "h-28")} />;
}

function PreviewLogo({ asset }: { asset: SponsoredBrandAsset | null }) {
  if (asset?.url) {
    return <img src={asset.url} alt="" className="size-12 shrink-0 rounded-sm object-contain" />;
  }
  return <div className="size-12 shrink-0 rounded-sm bg-muted" />;
}
